/**
 * Which roles may be a role's parent.
 *
 * The server's rule is the tree: `would_create_role_cycle` refuses the role
 * itself and any proposed parent whose ancestry passes through it, answering
 * 422 ROLE_CYCLE. The dropdown used to offer the whole list, so editing
 * "Recruiter" offered "Recruiter" and everything already under it — an action
 * the route exists to refuse.
 *
 * `roles` is the flat list `GET /roles` serves; a row carries `parentRoleId`,
 * and `parentRole.id` is read as a fallback for the shapes that only nest it.
 */
const parentIdOf = (role) => role?.parentRoleId ?? role?.parentRole?.id ?? null;

/** `roleId` and everything below it in the tree. */
export const roleAndDescendantIds = (roles, roleId) => {
    if (!roleId) return new Set();

    const blocked = new Set([roleId]);
    // The tree is shallow and the list is small, so a repeated sweep is
    // cheaper to read than an index — and it terminates on any input, which a
    // recursive walk over a cycle would not.
    let grew = true;
    while (grew) {
        grew = false;
        for (const role of roles) {
            const parent = parentIdOf(role);
            if (parent && blocked.has(parent) && !blocked.has(role.id)) {
                blocked.add(role.id);
                grew = true;
            }
        }
    }
    return blocked;
};

export const parentRoleOptions = (roles = [], editingRoleId = null) => {
    const blocked = roleAndDescendantIds(roles, editingRoleId);

    return roles
        .filter((role) => !blocked.has(role.id))
        .map((role) => ({ label: role.name, value: role.id }));
};
